import { useState, useEffect, useCallback, useRef } from 'react';
import { searchProspects, listProspects, generateBriefs, deleteProspect } from '../api/prospects';
import { useActiveProfile } from '../context/ActiveProfileContext';

export default function useProspects() {
  const { activeProfileId, setActiveProfileId } = useActiveProfile();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [briefing, setBriefing] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async (profileId) => {
    try {
      setError(null);
      const query = profileId ? { voiceProfileJobId: profileId } : {};
      const data = await listProspects(query);
      setProspects(data.prospects || []);
      setTotal(data.total || 0);
      return data.total || 0;
    } catch (err) {
      setError(err.message);
      return -1;
    }
  }, []);

  // Initial load (all prospects)
  useEffect(() => {
    setLoading(true);
    load(activeProfileId).finally(() => setLoading(false));
  }, [load, activeProfileId]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => stopPolling, [stopPolling]);

  const filterByProfile = useCallback((profileId) => {
    setActiveProfileId(profileId || null);
  }, [setActiveProfileId]);

  const search = useCallback(async (params) => {
    try {
      setSearching(true);
      setError(null);
      stopPolling();

      // Track which profile we're searching for
      const searchProfileId = params.voiceProfileJobId || null;
      setActiveProfileId(searchProfileId);
      await searchProspects(params);

      // Poll every 3s. Stop when count stabilizes (unchanged for 3 consecutive polls)
      let stableCount = 0;
      let lastTotal = -1;
      let attempts = 0;

      pollRef.current = setInterval(async () => {
        attempts++;
        const newTotal = await load(searchProfileId);

        if (newTotal === lastTotal) {
          stableCount++;
        } else {
          stableCount = 0;
        }
        lastTotal = newTotal;

        // Stop: count stable for 3 polls (~9s no change) OR 5 min timeout
        if ((stableCount >= 3 && newTotal > 0) || attempts >= 100) {
          stopPolling();
          setSearching(false);
        }
      }, 3000);
    } catch (err) {
      setSearching(false);
      setError(err.message);
    }
  }, [load, stopPolling, setActiveProfileId]);

  const brief = useCallback(async (prospectIds, icpCriteria, voiceProfileJobId) => {
    try {
      setBriefing(true);
      setError(null);
      stopPolling();
      await generateBriefs(prospectIds, icpCriteria, voiceProfileJobId);

      const targetIds = new Set(prospectIds);
      let stableCount = 0;
      let lastBriefed = -1;
      let attempts = 0;

      pollRef.current = setInterval(async () => {
        attempts++;
        const query = activeProfileId ? { voiceProfileJobId: activeProfileId } : {};
        const data = await listProspects(query);
        setProspects(data.prospects || []);
        setTotal(data.total || 0);

        const briefedNow = (data.prospects || []).filter(
          p => targetIds.has(p.prospectId) && p.status === 'briefed'
        ).length;

        if (briefedNow === lastBriefed) {
          stableCount++;
        } else {
          stableCount = 0;
        }
        lastBriefed = briefedNow;

        if ((stableCount >= 3 && briefedNow > 0) || attempts >= 40) {
          stopPolling();
          setBriefing(false);
        }
      }, 5000);
    } catch (err) {
      setBriefing(false);
      setError(err.message);
    }
  }, [stopPolling, activeProfileId]);

  const remove = useCallback(async (prospectId) => {
    try {
      await deleteProspect(prospectId);
      setProspects(prev => prev.filter(p => p.prospectId !== prospectId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const removeBatch = useCallback(async (prospectIds) => {
    try {
      setError(null);
      const idSet = new Set(prospectIds);
      await Promise.all(prospectIds.map(id => deleteProspect(id)));
      setProspects(prev => prev.filter(p => !idSet.has(p.prospectId)));
      setTotal(prev => prev - prospectIds.length);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  return {
    prospects, total, loading, searching, briefing, error,
    activeProfileId, search, brief, remove, removeBatch,
    reload: () => load(activeProfileId),
    filterByProfile,
  };
}
